import { Permissions } from "@cnx/common";
import { Flex } from "@mantine/core";
import * as Sentry from "@sentry/react";
import {
  IconCarCrash,
  IconClipboardPlus,
  IconReceiptEuro,
} from "@tabler/icons-react";
import FeatureSendEmail from "components/FeatureSendEmail";
import {
  FeatureSendTrafficAccidentReport,
  TrafficAccidentReportForm,
} from "components/FeatureSendTrafficAccidentReport";
import PrivateContent from "components/PrivateContent";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApplicationMessageForm } from "types/ApplicationMessage";
import { showError, showSuccess } from "utils/index";
import ApplicationMessageService from "../../services/ApplicationMessageService/ApplicationMessageService";
import GridTile from "./GridTile";

interface WidgetReportDocumentsProps {}

const WidgetReportDocuments: React.FC<WidgetReportDocumentsProps> = ({}) => {
  const { t } = useTranslation("news");
  const { t: tMessage } = useTranslation("message");
  const [isPending, setIsPending] = useState(false);

  const onSendReceipt = async (message: ApplicationMessageForm) => {
    try {
      setIsPending(true);
      await ApplicationMessageService.sendReceiptsMessage(message);
      showSuccess(tMessage("sentSuccessfully"));
    } catch (error) {
      showError(tMessage("sendMessageFailed"));
      Sentry.captureException(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };
  const onSendTrafficAccidentReport = async (
    values: TrafficAccidentReportForm
  ) => {
    try {
      setIsPending(true);
      await ApplicationMessageService.sendTrafficAccidentReportMessage(values);
      showSuccess(tMessage("sentSuccessfully"));
    } catch (error) {
      showError(tMessage("sendMessageFailed"));
      Sentry.captureException(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  const onSendSickLeave = async (message: ApplicationMessageForm) => {
    try {
      setIsPending(true);
      await ApplicationMessageService.sendSickLeaveMessage(message);
      showSuccess(tMessage("sentSuccessfully"));
    } catch (error) {
      showError(tMessage("sendMessageFailed"));
      Sentry.captureException(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  const renderTitle = (title: string, icon: React.ReactElement) => {
    return (
      <Flex gap={4}>
        {icon}
        {title}
      </Flex>
    );
  };

  return (
    <>
      <PrivateContent rights={[Permissions.RIGHTS_SEND_RECEIPTS]}>
        <div className="flex flex-1">
          <FeatureSendEmail
            title={renderTitle(
              t("sendReceipts"),
              <IconReceiptEuro className="text-green" />
            )}
            onSend={onSendReceipt}
            trigger={
              <GridTile
                component="button"
                icon={<IconReceiptEuro className="text-green" />}
                label={t("sendReceipts")}
              />
            }
            isPending={isPending}
          />
        </div>
      </PrivateContent>
      <PrivateContent rights={[Permissions.RIGHTS_SEND_SICK_LEAVE_CERTIFICATE]}>
        <div className="flex flex-1">
          <FeatureSendEmail
            title={renderTitle(
              t("sendSickLeaveCertificate"),
              <IconClipboardPlus className="text-green" />
            )}
            trigger={
              <GridTile
                component="button"
                icon={<IconClipboardPlus className="text-green" />}
                label={t("sendSickLeaveCertificate")}
              />
            }
            isPending={isPending}
            onSend={onSendSickLeave}
          />
        </div>
      </PrivateContent>
      <PrivateContent
        rights={[Permissions.RIGHTS_SEND_TRAFFIC_ACCIDENT_REPORT]}
      >
        <div className="flex flex-1 col-span-2">
          <FeatureSendTrafficAccidentReport
            onSend={onSendTrafficAccidentReport}
            isPending={isPending}
            trigger={
              <GridTile
                component="button"
                icon={<IconCarCrash className="text-green" />}
                label={t("sendTrafficAccidentReport")}
              />
            }
          />
        </div>
      </PrivateContent>
    </>
  );
};

export default WidgetReportDocuments;
